import { Link } from "react-router-dom";
import { Check, ArrowUpRight, Handshake } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { Icon } from "@/lib/icons";
import { SERVICES, img } from "@/config";

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Our Services"
        titleLines={["Complete security &", "IT infrastructure."]}
        subtitle="Eight core capabilities, one accountable partner. Engage us for a single deployment or as your long-term technology partner across every site."
        imageKey="hero_cctv"
      >
        <div className="mt-8 flex flex-wrap gap-2.5">
          {SERVICES.map((s) => (
            <a key={s.slug} href={`#${s.slug}`} className="rounded-full border border-slate-700 bg-white/5 px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:border-[#2563EB] hover:text-white">{s.title}</a>
          ))}
        </div>
      </PageHero>

      <div>
        {SERVICES.map((service, i) => {
          const reversed = i % 2 === 1;
          return (
            <section key={service.slug} id={service.slug} data-testid={`service-detail-${service.slug}`} className="scroll-mt-28 border-t border-slate-800 py-20 lg:py-24">
              <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className={`grid items-center gap-12 lg:grid-cols-2 lg:gap-16 ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <div>
                    <Reveal>
                      <div className="flex items-center gap-4">
                        <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-800 bg-white/5 text-[#60A5FA]"><Icon name={service.icon} className="h-6 w-6" /></span>
                        <span className="font-display text-sm font-bold uppercase tracking-widest text-slate-500">0{i + 1}</span>
                      </div>
                    </Reveal>
                    <Reveal delay={0.1}><h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white lg:text-4xl">{service.title}</h2></Reveal>
                    <Reveal delay={0.16}><p className="mt-5 max-w-lg text-lg leading-relaxed text-slate-400">{service.description}</p></Reveal>
                    <Reveal delay={0.22}>
                      <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                        {service.features.map((f) => (
                          <li key={f} className="flex items-start gap-3 text-slate-300">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2563EB]/20 text-[#60A5FA]"><Check className="h-3 w-3" /></span>
                            <span className="text-sm">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </Reveal>
                    {service.partners.length > 0 && (
                      <Reveal delay={0.26}>
                        <div className="mt-8 flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500"><Handshake className="h-4 w-4" /> Brand partners:</span>
                          {service.partners.map((p) => (
                            <span key={p} className="rounded-md border border-slate-800 bg-white/5 px-2.5 py-1 text-xs font-semibold text-slate-300">{p}</span>
                          ))}
                        </div>
                      </Reveal>
                    )}
                    <Reveal delay={0.3}>
                      <Link to="/contact" data-testid={`service-cta-${service.slug}`} className="group mt-9 inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#3b82f6]">
                        Enquire about {service.title.split(" ")[0]} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    </Reveal>
                  </div>

                  <Reveal delay={0.2}>
                    <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-slate-800">
                      <img src={img(service.image)} alt={service.title} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120]/70 to-transparent" />
                      <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                        {service.solutions.map((s) => (
                          <span key={s} className="rounded-full bg-[#0B1120]/80 px-3 py-1 text-xs font-medium text-slate-200 backdrop-blur-sm">{s}</span>
                        ))}
                      </div>
                    </div>
                  </Reveal>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
